import * as React from 'react';
import { Label } from '@highereducation/hen-ui';
import type { FieldError, GravityFormField } from '../types';

/** Shared props every field component receives from the dispatcher. */
export interface FieldComponentProps {
  field: GravityFormField;
  formId: number;
  error?: FieldError;
}

const isLabelHidden = (field: GravityFormField): boolean =>
  field.labelPlacement === 'HIDDEN';

/** Field label built on the hen-ui `Label` atomic; honors GF `HIDDEN` placement. */
export function FieldLabel({
  field,
  htmlFor,
}: {
  field: GravityFormField;
  htmlFor?: string;
}) {
  return (
    <Label
      htmlFor={htmlFor}
      required={Boolean(field.isRequired)}
      className={isLabelHidden(field) ? 'sr-only' : 'mb-xs block font-bold'}
    >
      {field.label}
    </Label>
  );
}

/** Group legend for fieldset-based fields (RADIO/CHECKBOX/CONSENT). */
export function FieldLegend({ field }: { field: GravityFormField }) {
  return (
    <legend
      className={
        isLabelHidden(field) ? 'sr-only' : 'mb-xs block font-bold text-heading'
      }
    >
      {field.label}
      {field.isRequired && (
        <span className='ml-1 text-error-500' aria-label='required'>
          *
        </span>
      )}
    </legend>
  );
}

/** Inline field-level error surfaced from the submit mutation. */
export function FieldErrorMessage({ error }: { error?: FieldError }) {
  if (!error) return null;
  return (
    <div
      className='mt-xxs w-full border border-error-500 bg-error-100 px-md py-xs text-b5 text-error-500'
      role='alert'
    >
      {error.message}
    </div>
  );
}
